import { type FC, useCallback, useMemo, useState } from "react";

import { formatAbbrevNumber } from "../../utils/currency";
import { Button } from "../common/Button";
import { SIGNAL_HEX } from "../common/colorTokens";
import { Modal } from "../common/Modal";
import { NewRoomDialog } from "../rooms/NewRoomDialog";
import { ChannelTag, ChannelToggleTag } from "./ChannelTag";
import { getAvatarBackgroundColor, InstanceAvatar } from "./InstanceAvatar";
import { ModelChip } from "./ModelChip";
import { ModelPicker } from "./ModelPicker";
import { CornerBrackets, RetroError, SectionLabel } from "./retro";
import { type OrgChannelSummary, useOrgChannels } from "./useOrgChannels";
import { type ActorTemplate, useTemplates } from "./useTemplates";

export interface SpawnInput {
  /** Required: every actor instance must belong to an org-scoped template.
   * The orchestrator rejects spawn requests without a template_id. */
  templateId: string;
  avatarSeed: string;
  displayName: string;
  identityPrompt: string | null;
  modelRef?: string;
  sharedChannelId?: string;
  templateModelRef: string | null;
}

interface SpawnDialogProps {
  actionError: string | null;
  initialSharedChannelId?: string | null;
  onClose: () => void;
  onSpawn: (input: SpawnInput) => Promise<void>;
  spawning: boolean;
}

export const SpawnDialog: FC<SpawnDialogProps> = ({
  actionError,
  initialSharedChannelId = null,
  onClose,
  onSpawn,
  spawning,
}) => {
  const { templates, loading: templatesLoading } = useTemplates();
  const [templateId, setTemplateId] = useState("");
  const [templateQuery, setTemplateQuery] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [modelRefOverride, setModelRefOverride] = useState("");
  const [selectedSharedChannelId, setSelectedSharedChannelId] = useState<
    string | null
  >(initialSharedChannelId);
  const [creatingRoom, setCreatingRoom] = useState(false);
  // A room created from inside this dialog may not be in the realtime-driven
  // channel list yet — keep it locally so the selected tag renders immediately.
  const [justCreatedRoom, setJustCreatedRoom] =
    useState<OrgChannelSummary | null>(null);
  const {
    channels: orgChannels,
    loading: channelsLoading,
    error: channelsError,
  } = useOrgChannels();
  const joinableOrgChannels = orgChannels.filter(
    (channel) =>
      channel.kind === "shared" &&
      channel.deletedAt == null &&
      (channel.archivedAt == null ||
        channel.archivedReason === "dormant_memberless"),
  );

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === templateId) ?? null,
    [templateId, templates],
  );

  const accentColor = useMemo(
    () =>
      selectedTemplate
        ? getAvatarBackgroundColor(selectedTemplate.avatarSeed)
        : SIGNAL_HEX.healthy,
    [selectedTemplate],
  );

  const handleSelectTemplate = useCallback(
    (nextTemplateId: string) => {
      setTemplateId(nextTemplateId);
      if (!nextTemplateId) {
        setDisplayName("");
        setModelRefOverride("");
        return;
      }
      const tpl = templates.find((t) => t.id === nextTemplateId);
      if (tpl) {
        setDisplayName(tpl.name);
        setModelRefOverride("");
      }
    },
    [templates],
  );

  const toggleChannel = useCallback((channelId: string) => {
    setSelectedSharedChannelId((prev) =>
      prev === channelId ? null : channelId,
    );
  }, []);

  const handleSpawn = useCallback(() => {
    const trimmed = displayName.trim();
    if (!trimmed || !selectedTemplate) {
      return;
    }
    void onSpawn({
      avatarSeed: selectedTemplate.avatarSeed,
      displayName: trimmed,
      identityPrompt: selectedTemplate.identityPrompt,
      ...(selectedSharedChannelId && {
        sharedChannelId: selectedSharedChannelId,
      }),
      ...(modelRefOverride && { modelRef: modelRefOverride }),
      templateId: selectedTemplate.id,
      templateModelRef: selectedTemplate.modelRef,
    });
  }, [
    displayName,
    modelRefOverride,
    selectedSharedChannelId,
    selectedTemplate,
    onSpawn,
  ]);

  const selectedSharedChannel = useMemo(
    () =>
      orgChannels.find((ch) => ch.id === selectedSharedChannelId) ??
      (justCreatedRoom?.id === selectedSharedChannelId
        ? justCreatedRoom
        : null),
    [orgChannels, selectedSharedChannelId, justCreatedRoom],
  );

  const selectedSharedChannelTag = selectedSharedChannel && (
    <div className="mb-2 flex flex-wrap gap-1.5">
      <ChannelTag
        channel={selectedSharedChannel}
        onLeave={() => toggleChannel(selectedSharedChannel.id)}
      />
    </div>
  );

  const sharedChannelContent = (() => {
    if (channelsLoading) {
      return (
        <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-violet-700/80 dark:text-violet-300/60">
          [ LOADING CHANNELS… ]
        </p>
      );
    }
    if (joinableOrgChannels.length === 0) {
      return (
        <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
          [ NO ORG CHANNELS YET ]
        </p>
      );
    }
    return (
      <div className="mb-2 flex flex-wrap gap-1">
        {joinableOrgChannels.map((ch) => (
          <ChannelToggleTag
            key={ch.id}
            channel={ch}
            active={selectedSharedChannelId === ch.id}
            onToggle={() => toggleChannel(ch.id)}
          />
        ))}
      </div>
    );
  })();

  return (
    <>
      <Modal
        title="Spawn Agent"
        subtitle="Spin up an instance from an actor template"
        onClose={onClose}
        widthClass="max-w-md"
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              size="sm"
              loading={spawning}
              loadingText="Spawning..."
              disabled={!displayName.trim() || !templateId}
              onClick={handleSpawn}
            >
              Spawn
            </Button>
          </div>
        }
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSpawn();
          }}
          className="space-y-5"
        >
          {actionError && <RetroError message={actionError} />}
          {/* Template selector */}
          <div>
            <SectionLabel label="TEMPLATE" accent="emerald" className="mb-2" />
            {(() => {
              if (templatesLoading) {
                return (
                  <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-700/70 dark:text-emerald-300/60">
                    [ LOADING TEMPLATES… ]
                  </p>
                );
              }
              if (templates.length === 0) {
                return (
                  <RetroError message="No templates available. Ask an admin to create one." />
                );
              }
              // Cards, not a native select: the template choice is the
              // dialog's main decision — give it faces, model chips, and a
              // readable description line instead of em-dash option soup.
              const visibleTemplates = filterTemplates(
                templates,
                templateQuery,
              );
              return (
                <div className="grid gap-1.5">
                  {templates.length > TEMPLATE_FILTER_THRESHOLD && (
                    <input
                      type="search"
                      value={templateQuery}
                      onChange={(e) => setTemplateQuery(e.target.value)}
                      placeholder={`filter ${templates.length} templates…`}
                      aria-label="Filter templates"
                      className="w-full border border-slate-200 bg-slate-50 px-2.5 py-1.5 font-mono text-[11px] text-slate-900 placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none dark:border-slate-700 dark:bg-slate-900/60 dark:text-white dark:placeholder:text-slate-600"
                    />
                  )}
                  {visibleTemplates.length === 0 && (
                    <p className="border border-dashed border-slate-300/70 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500 dark:border-slate-700/70">
                      no templates match "{templateQuery.trim()}"
                    </p>
                  )}
                  <SpawnTemplateGrid
                    visibleTemplates={visibleTemplates}
                    templateId={templateId}
                    handleSelectTemplate={handleSelectTemplate}
                  />
                </div>
              );
            })()}
          </div>

          {/* Template preview — read-only summary of what's coming through */}
          {selectedTemplate && (
            <TemplatePreview
              template={selectedTemplate}
              accentColor={accentColor}
            />
          )}

          {/* Display name — instance-level rename of the template */}
          {selectedTemplate && (
            <div>
              <SectionLabel
                label="DISPLAY NAME"
                accent="emerald"
                className="mb-2"
              />
              <input
                id="instance-name"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g. Auth Reviewer #2"
                className="w-full border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500/30 dark:border-slate-700 dark:bg-slate-900/60 dark:text-white dark:placeholder:text-slate-600 dark:focus:border-emerald-400/60 dark:focus:bg-slate-900 dark:focus:ring-emerald-400/30"
              />
              <p className="mt-1.5 font-mono text-[10px] tracking-tight text-slate-500">
                Defaults to template name; rename to disambiguate multiple
                instances.
              </p>
            </div>
          )}

          {selectedTemplate && (
            <div>
              <SectionLabel label="MODEL" accent="emerald" className="mb-2" />
              <ModelPicker
                id="spawn-model"
                value={modelRefOverride}
                onChange={setModelRefOverride}
                showTemplateDefault
              />
              <p className="mt-1.5 font-mono text-[10px] tracking-tight text-slate-500">
                Defaults to the template model; override for this spawn only.
              </p>
            </div>
          )}

          {/* Optional shared channel — every actor gets a private channel. */}
          {selectedTemplate && (
            <div>
              <SectionLabel
                label="SHARED CHANNEL"
                accent="violet"
                meta={
                  selectedSharedChannel ? (
                    <span>selected</span>
                  ) : (
                    <span className="text-slate-400">optional</span>
                  )
                }
                className="mb-2"
              />

              {channelsError ? (
                <RetroError message={channelsError} />
              ) : (
                <>
                  {selectedSharedChannelTag}

                  {sharedChannelContent}
                  <button
                    type="button"
                    onClick={() => setCreatingRoom(true)}
                    className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-700 transition-colors hover:text-emerald-600 dark:text-emerald-300 dark:hover:text-emerald-200"
                  >
                    + New channel
                  </button>
                  <p className="mt-1.5 font-mono text-[10px] tracking-tight text-slate-500">
                    Leave empty for a solo/private actor, pick a shared channel,
                    or create one.
                  </p>
                </>
              )}
            </div>
          )}
        </form>
      </Modal>
      {creatingRoom && (
        <NewRoomDialog
          onClose={() => setCreatingRoom(false)}
          onCreated={(channel) => {
            setJustCreatedRoom(channel);
            setSelectedSharedChannelId(channel.id);
            setCreatingRoom(false);
          }}
        />
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Read-only template preview — shows what the user is spawning so they can't
// Be surprised. Not editable here; edit the template if you need to change it.
// ---------------------------------------------------------------------------

const TemplatePreview: FC<{ template: ActorTemplate; accentColor: string }> = ({
  template,
  accentColor,
}) => {
  const identitySummary = (() => {
    const prompt = template.identityPrompt;
    if (!prompt) {
      return null;
    }
    return prompt.length > 140 ? `${prompt.slice(0, 140).trim()}…` : prompt;
  })();

  return (
    <div
      className="relative border bg-slate-100/60 px-3 py-3 dark:bg-slate-900/40"
      style={{ borderColor: `${accentColor}33` }}
    >
      <CornerBrackets color={accentColor} />
      <div className="flex items-start gap-3">
        <div
          className="flex-shrink-0"
          style={{ filter: `drop-shadow(0 0 8px ${accentColor}44)` }}
        >
          <InstanceAvatar
            seed={template.avatarSeed}
            size="md"
            status="waiting"
          />
        </div>
        <div className="min-w-0 flex-1">
          <p
            className="font-mono text-[9px] font-bold uppercase tracking-[0.25em]"
            style={{ color: accentColor }}
          >
            [ FROM TEMPLATE ]
          </p>
          <p className="mt-0.5 truncate font-mono text-sm font-semibold text-slate-900 dark:text-white">
            {template.name}
          </p>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {template.modelRef ? (
              <ModelChip modelRef={template.modelRef} />
            ) : (
              <Chip label="default" color="violet" />
            )}
            {template.isSingleton && <Chip label="solo" color="emerald" />}
            <Chip
              label={`buf ${formatAbbrevNumber(template.compactContextGrowthThreshold, "exact")}`}
              color="sky"
            />
          </div>
          {identitySummary && (
            <p className="mt-2 line-clamp-3 font-mono text-[10.5px] leading-relaxed text-slate-400">
              {identitySummary}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const Chip: FC<{ label: string; color: "violet" | "sky" | "emerald" }> = ({
  label,
  color,
}) => {
  const cls = {
    emerald:
      "border-emerald-500/40 text-emerald-700 dark:border-emerald-400/30 dark:text-emerald-300",
    sky: "border-sky-500/40 text-sky-700 dark:border-sky-400/30 dark:text-sky-300",
    violet:
      "border-violet-500/40 text-violet-700 dark:border-violet-400/30 dark:text-violet-300",
  }[color];
  return (
    <span
      className={`inline-flex items-center border px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.2em] ${cls}`}
    >
      {label}
    </span>
  );
};

/** Threshold above which the template picker grows a filter box — a short
 *  list needs no chrome; a long one needs findability the cards alone
 *  (unlike a native select's type-to-jump) don't provide. */
export const TEMPLATE_FILTER_THRESHOLD = 6;

export function filterTemplates<
  T extends {
    name: string;
    description: string | null;
    modelRef: string | null;
  },
>(templates: readonly T[], query: string): readonly T[] {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return templates;
  }
  return templates.filter((template) =>
    [template.name, template.description ?? "", template.modelRef ?? ""]
      .join(" ")
      .toLowerCase()
      .includes(needle),
  );
}

interface SpawnTemplateOptionProps {
  template: ActorTemplate;
  selected: boolean;
  onSelect: (id: string) => void;
}

function SpawnTemplateOption({
  template,
  selected,
  onSelect,
}: SpawnTemplateOptionProps) {
  return (
    <button
      key={template.id}
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={() => onSelect(template.id)}
      className={`flex min-w-0 items-start gap-2 border px-2.5 py-2 text-left transition ${
        selected
          ? "border-emerald-400 bg-emerald-400/10"
          : "border-slate-200 bg-slate-50/70 hover:border-emerald-400/60 dark:border-slate-700 dark:bg-slate-900/50"
      }`}
    >
      <span className="shrink-0">
        <InstanceAvatar seed={template.avatarSeed} size="sm" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-1.5">
          <span
            className={`min-w-0 truncate font-mono text-[11px] font-bold uppercase tracking-[0.14em] ${
              selected
                ? "text-emerald-800 dark:text-emerald-200"
                : "text-slate-700 dark:text-slate-200"
            }`}
          >
            {template.name}
          </span>
          {template.modelRef && (
            <span className="shrink-0">
              <ModelChip modelRef={template.modelRef} />
            </span>
          )}
        </span>
        {template.description && (
          <span className="mt-0.5 block truncate font-mono text-[9px] tracking-tight text-slate-500 dark:text-slate-400">
            {template.description}
          </span>
        )}
      </span>
    </button>
  );
}

interface SpawnTemplateGridProps {
  visibleTemplates: readonly ActorTemplate[];
  templateId: string;
  handleSelectTemplate: (id: string) => void;
}

function SpawnTemplateGrid({
  visibleTemplates,
  templateId,
  handleSelectTemplate,
}: SpawnTemplateGridProps) {
  return (
    <div
      id="template-select"
      role="radiogroup"
      aria-label="Actor template"
      className="grid max-h-72 gap-1.5 overflow-y-auto pr-1 sm:grid-cols-2"
    >
      {visibleTemplates.map((t) => {
        const selected = t.id === templateId;
        return (
          <SpawnTemplateOption
            key={t.id}
            template={t}
            selected={selected}
            onSelect={handleSelectTemplate}
          />
        );
      })}
    </div>
  );
}
